import React, {useEffect, useMemo} from "react";
import {Badge, Label, Offcanvas, OffcanvasBody, OffcanvasHeader} from "reactstrap";
import Select from "../../../../components/Common/Select";
import NoSearchResult from "../../../../components/Common/NoSearchResult";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import * as yup from "yup";
import {ICommonOrder, ICommonOrderAssignForm, ICommonOrderUpdate} from "../../../../store/commonOrders/types";
import {useFormik} from "formik";
import {CommonOrderService} from "../../../../services/common_order.service";
import {useDispatch, useSelector} from "react-redux";
import {IOffice} from "../../../../store/offices/types";
import {clearOffices, getOffices} from "../../../../store/offices/actions";

interface AssignToCommonOrderProps {
    orderId: number,
    officeId: number,
    commonOrderId: number,
    isOpen: boolean,
    toggle: () => void,
}

const AssignToCommonOrder: React.FC<AssignToCommonOrderProps> = ({
                                                                     orderId,
                                                                     officeId,
                                                                     commonOrderId,
                                                                     isOpen,
                                                                     toggle
                                                                 }) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const offices = useSelector((state: any) => (state.Offices.data || []) as Array<IOffice>);
    const loadingOffices = useSelector((state: any) => state.Offices.loading || false);

    useEffect(() => {
        dispatch(getOffices('', {}))

        return () => {
            dispatch(clearOffices())
        };
    }, []);

    const {
        data: trafficOrders,
        isLoading: loadingOrders,
    } = useQuery({
        queryKey: ["keyTrafficOrders"],
        queryFn: () => TrafficOrderService.getTrafficOrders(),
        retry: false,
    });

    const {
        data: commonOrder,
        isLoading: loadingCommonOrder,
    } = useQuery({
        queryKey: ["keyCommonOrder"],
        queryFn: () => CommonOrderService.getCommonOrder(commonOrderId),
        retry: false,
    });

    const {
        mutate: updateCommonOrder,
        isSuccess: isSuccessUpdate,
        isPending: isPendingUpdate,
    } = useMutation({
        mutationFn: (data: ICommonOrderUpdate) => CommonOrderService.updateCommonOrder(commonOrderId, data),
        mutationKey: ['keyUpdateCommonOrder', commonOrderId]
    });

    const ordersSelect = useMemo(() => (
        trafficOrders?.data.filter((to: any) => to.office_id === officeId)
            .map((item: any) => ({
                value: item.id,
                label: `[${item.id}] | ${item.broker.name}`,
                office_id: item.office_id
            }))
    ), [trafficOrders?.data]);

    useEffect(() => {
        if (isSuccessUpdate) {
            toggle()
            queryClient.invalidateQueries({queryKey: ['keyCommonOrders']})
        }
    }, [isSuccessUpdate]);

    const validationSchema: yup.Schema<ICommonOrderAssignForm> = yup.object().shape({
        order_id: yup.number()
            .min(1, "select an order")
            .required("required")
    });

    const initialValues: ICommonOrderAssignForm = {
        order_id: orderId,
    }

    const handleSubmitForm = (value: ICommonOrderAssignForm) => {
        const result = (commonOrder as ICommonOrder).data.map(co =>
            co.office_id === officeId ? {...co, order_id: value.order_id} : co
        );

        updateCommonOrder({...commonOrder, data: result})
    }

    const {
        values,
        handleSubmit,
        isValid,
        setFieldValue
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const getOfficeColor = (order: any) => {
        if (order.office_id === officeId) return "info"
        if (order.order_id === null) return "dark"
        return "success"
    }

    const loading = !loadingOrders && !loadingCommonOrder && !loadingOffices;

    return (
        <Offcanvas isOpen={isOpen} direction="end" toggle={toggle}>
            <OffcanvasHeader toggle={toggle}> Assign Order | {commonOrderId} </OffcanvasHeader>

            {loading ? (
                <OffcanvasBody>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-2 p-3 border rounded">
                            <h3 className="m-0 text-center">{commonOrder?.name}</h3>
                        </div>

                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2 p-2 border rounded">
                            {commonOrder.data.map((order: any) => (
                                <h3 key={order.office_id} className="m-0">
                                    <Badge color={getOfficeColor(order)}>
                                        {offices.find(o => o.id === order.office_id)?.name}
                                    </Badge>
                                </h3>
                            ))}
                        </div>

                        <div className='mb-3'>
                            <Label>Order: </Label>
                            <Select
                                isSearchable
                                name="order_id"
                                label="Order"
                                placeholder="Order"
                                options={ordersSelect}
                                onChange={(item: any) => setFieldValue("order_id", item.value)}
                                value={ordersSelect.find((order: any) => values.order_id === order.value)}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary w-100" disabled={!isValid || isPendingUpdate}>
                            {isPendingUpdate ? "Loading..." : "Save"}
                        </button>
                    </form>
                </OffcanvasBody>
            ) : (
                <NoSearchResult text="Loading ..."/>
            )}
        </Offcanvas>
    )
}

export default AssignToCommonOrder;